// book-availability.js -- per-book SlipSync kill switch (bridge-hardening spec §6). Same pattern as legalVersions.js.
// Single job: answer "is this book's sync enabled?" from public/slipsync-books.json on our site, read within 3 s,
// falling back to the last good copy, else allowing (an outage on our side must not block every user -- accepted
// V1 weakness). Never rejects. The SPA rewrite answers an undeployed file with text/html: treated as unreadable.
import * as Sentry from '../errorReporting';
import { PUBLIC_WEB_BASE } from '../api';
import { storageGet, storageSet } from '../platform';

export const BOOKS_FILE_DEADLINE_MS = 3000;
const CACHE_KEY = 'slipsync_books_v1';
const REQUIRED = ['mgm', 'dk', 'fan'];

export function isValidBooksFile(v) {
  if (!v || typeof v !== 'object' || v.version !== 1 || !v.books || typeof v.books !== 'object') return false;
  return REQUIRED.every((k) => v.books[k] && typeof v.books[k].enabled === 'boolean');
}
function cached() {
  try { const v = JSON.parse(storageGet(CACHE_KEY) || 'null'); return isValidBooksFile(v) ? v : null; }
  catch { return null; } // corrupt cache: ignored (expected control flow)
}
async function readRemote(fetchImpl, timeoutMs) {
  const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timer;
  const deadline = new Promise((_, reject) => { timer = setTimeout(() => { if (ctl) ctl.abort(); reject(new Error('kill-switch read timed out')); }, timeoutMs); });
  try {
    const res = await Promise.race([deadline, fetchImpl(`${PUBLIC_WEB_BASE}/slipsync-books.json`, { cache: 'no-store', signal: ctl ? ctl.signal : undefined })]);
    if (!res || !res.ok) throw new Error('kill-switch read non-ok');
    const ct = (res.headers && res.headers.get && res.headers.get('content-type')) || '';
    if (!ct.includes('application/json')) throw new Error('kill-switch read non-JSON');
    const body = await Promise.race([deadline, res.json()]);
    if (!isValidBooksFile(body)) throw new Error('kill-switch file invalid');
    return body;
  } finally { clearTimeout(timer); }
}
export async function isBookEnabled(bookKey, { fetchImpl = fetch, timeoutMs = BOOKS_FILE_DEADLINE_MS } = {}) {
  let file = null;
  try {
    file = await readRemote(fetchImpl, timeoutMs);
    storageSet(CACHE_KEY, JSON.stringify(file));
  } catch {
    Sentry.captureMessage('slipsync: kill-switch read failed', { level: 'warning', tags: { function: 'isBookEnabled' } });
    file = cached();
  }
  if (!file || !file.books[bookKey]) return true;
  return file.books[bookKey].enabled === true;
}
